import React, { useState, useMemo } from "react";
import { Button, Typography, Snackbar, Alert, CircularProgress, TextField, Box } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import CartItem from "../Cart/CartItem";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getOrderById, placedOrder } from "../../../Redux/Customers/Order/Action";
import AddressCard from "../adreess/AdreessCard";
import { clearUserCart } from "../../../Redux/Customers/Cart/Action";
import OrderConfirmationModal from "./OrderConfirmationModal";
import { BOX_WEIGHTS } from "../../../utils/boxPricing";

const OrderSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const orderId = searchParams.get("order_id");
  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");
  const { order } = useSelector(state => state);

  // Modal and loading states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Compute total weight from order items box sizes
  const totalWeight = useMemo(() => {
    if (!order.order?.orderItems) return 0;
    return order.order.orderItems.reduce((sum, item) => {
      const weight = BOX_WEIGHTS[item?.size] || 0;
      return sum + (weight * (item?.quantity || 1));
    }, 0);
  }, [order.order?.orderItems]);


  useEffect(() => {
    dispatch(getOrderById(orderId));
  }, [orderId, dispatch]);

  const handleOpenConfirmModal = () => {
    setShowConfirmModal(true);
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
  };

  const handleConfirmOrder = async () => {
    setIsPlacingOrder(true);

    try {
      // Confirm order, send email, and clear backend cart
      await dispatch(placedOrder(order.order?.id, specialInstructions));

      // Clear user's cart in Redux state

      dispatch(clearUserCart(jwt));

      setSnackbar({
        open: true,
        message: "Order placed successfully! Confirmation email sent.",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to place order. Please try again.",
        severity: "error",
      });
    }

    setIsPlacingOrder(false);
    setShowConfirmModal(false);

    // Navigate to orders page after a brief delay
    setTimeout(() => {
      navigate("/account/order");
    }, 2000);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div className="space-y-5">
      <div className="p-5 shadow-lg rounded-md border ">
        <AddressCard address={order.order?.shippingAddress} />
      </div>
      <div className="lg:grid grid-cols-3 relative justify-between">
        <div className="lg:col-span-2 ">
          <div className="space-y-3">
            {order.order?.orderItems?.map((item) => (
              <CartItem key={item.id} item={item} showButton={false} />
            ))}
          </div>
        </div>
        <div className="sticky top-0 h-[100vh] mt-5 lg:mt-0 ml-5">
          <div className="border p-5 bg-white shadow-lg rounded-md">
            <Typography variant="h6" className="font-bold opacity-60 pb-4">PRICE DETAILS</Typography>
            <hr />

            <div className="space-y-3 font-semibold">
              <div className="flex justify-between pt-3 text-black">
                <span>Items ({order.order?.totalItem} {order.order?.totalItem === 1 ? 'item' : 'items'})</span>
                <span>${typeof order.order?.totalPrice === 'number' ? order.order.totalPrice.toFixed(2) : (order.order?.totalPrice || 0)}</span>
              </div>
              <div className="flex justify-between text-black">
                <span>Total Weight</span>
                <span>{totalWeight} lbs</span>
              </div>
              <div className="flex justify-between">
                <span>Discount</span>
                <span className="text-green-700">-${typeof order.order?.discount === 'number' ? order.order.discount.toFixed(2) : (order.order?.discount || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span>Delivery Charges</span>
                <span className="text-blue-600 text-sm italic">Calculated at the Time of Delivery</span>
              </div>
              <hr />
              <div className="flex justify-between font-bold text-lg">
                <span>Total Amount</span>
                <span className="text-green-700">${typeof order.order?.totalDiscountedPrice === 'number' ? order.order.totalDiscountedPrice.toFixed(2) : (order.order?.totalDiscountedPrice || 0)}</span>
              </div>
            </div>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", opacity: 0.7 }}>
                SPECIAL INSTRUCTIONS (Optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Add any specific delivery notes, gate codes, or special requests here..."
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                inputProps={{ maxLength: 512 }}
              />
              <Typography variant="caption" sx={{ mt: 0.5, display: "block", textAlign: "right", opacity: 0.6 }}>
                {specialInstructions.length}/512 characters
              </Typography>
            </Box>

            <Button

              onClick={handleOpenConfirmModal}
              variant="contained"
              type="submit"
              disabled={isPlacingOrder}
              sx={{
                padding: ".8rem 2rem",
                marginTop: "2rem",
                width: "100%",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)",
                },
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              {isPlacingOrder ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Place Order"
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Order Confirmation Modal */}
      <OrderConfirmationModal
        open={showConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmOrder}
        order={order.order}
        isLoading={isPlacingOrder}
        specialInstructions={specialInstructions}
      />


      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default OrderSummary;
